import { auth } from "./firebase.js";
import { signOut } from "https://www.gstatic.com/firebasejs/10.13.2/firebase-auth.js";
import { api } from "./api.js";
import "./shared/format.js";

const { onboardingState, invitationCards, actionErrorMessage, signedInLabel } = window.TransportOSOnboarding;
const { escapeHtml } = window.TransportOSFormat;
const { t } = window.TransportOSI18n;

const screen = document.getElementById("screen-onboarding");
const els = {
  phone: document.getElementById("onboarding-phone"),
  notice: document.getElementById("onboarding-notice"),
  list: document.getElementById("onboarding-invites"),
  skip: document.getElementById("onboarding-skip"),
  skipError: document.getElementById("onboarding-skip-error"),
  signOut: document.getElementById("onboarding-sign-out"),
  signOutError: document.getElementById("onboarding-sign-out-error"),
};

screen.prepend(document.querySelector("#screen-signin > aside").cloneNode(true));

let onDone = () => {};
let busy = false;
let shownMe = null;

// While onboarding, GET /me with no open invitations left is what provisions the person's own org.
export async function loadMe() {
  const me = await api.get("/me");
  return onboardingState(me) === "provision" ? api.get("/me") : me;
}

function setError(el, message) {
  el.textContent = message || "";
  el.hidden = !message;
}

function cardHtml(card) {
  return `<div class="invite-card" data-invite-id="${escapeHtml(card.id)}">
    <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
      <div class="flex items-start gap-3.5 min-w-0">
        <div class="w-11 h-11 rounded bg-slate-900 text-white font-bold text-[14px] flex items-center justify-center flex-shrink-0 uppercase" aria-hidden="true">${escapeHtml(card.initials)}</div>
        <div class="min-w-0">
          <div class="flex items-center gap-2 min-w-0">
            <h2 class="text-[16px] font-bold text-slate-900 leading-snug break-words">${escapeHtml(card.orgName)}</h2>
            <span class="mono text-[11px] bg-blue-50 text-secondary font-medium px-2 py-0.5 rounded border border-blue-100 flex-shrink-0">${escapeHtml(t("onboarding.pending"))}</span>
          </div>
          ${card.invitedBy ? `<div class="mono text-[11px] text-slate-600 mt-1.5 flex items-center gap-1.5">
            <span class="material-symbols-outlined text-[14px] text-slate-400" aria-hidden="true">person</span><span>${escapeHtml(card.invitedBy)}</span>
          </div>` : ""}
          <div class="mono text-[11px] text-slate-500 mt-1 flex items-center gap-1.5">
            <span class="material-symbols-outlined text-[14px] text-slate-400" aria-hidden="true">schedule</span><span>${escapeHtml(card.meta)}</span>
          </div>
        </div>
      </div>
      <div class="flex items-center gap-2.5 flex-shrink-0 sm:pt-0.5">
        <button type="button" data-action="decline" class="onboarding-btn flex-1 sm:flex-none px-3.5 py-2 text-[13px] font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50">
          <span data-role="label">${escapeHtml(t("onboarding.decline"))}</span>
        </button>
        <button type="button" data-action="accept" class="onboarding-btn flex-1 sm:flex-none px-4 py-2 text-[13px] font-semibold text-white bg-slate-950 hover:bg-slate-800 border border-slate-950 shadow-sm whitespace-nowrap">
          <span data-role="label">${escapeHtml(card.joinLabel)}</span>
        </button>
      </div>
    </div>
    <p class="field-error" data-role="card-error" role="alert" hidden></p>
  </div>`;
}

function setBusy(activeButton, busyLabel) {
  busy = Boolean(activeButton);
  els.list.setAttribute("aria-busy", String(busy));
  screen.querySelectorAll(".onboarding-btn").forEach((button) => {
    button.disabled = busy;
    button.classList.toggle("is-busy", button === activeButton);
  });
  if (activeButton) {
    const label = activeButton.querySelector('[data-role="label"]');
    label.dataset.idle = label.textContent;
    label.textContent = busyLabel;
  } else {
    screen.querySelectorAll("[data-idle]").forEach((label) => {
      label.textContent = label.dataset.idle;
      delete label.dataset.idle;
    });
  }
}

function clearErrors() {
  setError(els.notice, "");
  setError(els.skipError, "");
  setError(els.signOutError, "");
  els.list.querySelectorAll('[data-role="card-error"]').forEach((el) => setError(el, ""));
}

function paint(me) {
  shownMe = me;
  els.phone.textContent = signedInLabel(me.phone || (auth.currentUser && auth.currentUser.phoneNumber));
  els.list.innerHTML = invitationCards(me.onboarding.invites, new Date()).map(cardHtml).join("");
  const firstJoin = els.list.querySelector('[data-action="accept"]');
  if (firstJoin) firstJoin.focus({ preventScroll: true });
}

async function settle(me) {
  const next = onboardingState(me) === "provision" ? await api.get("/me") : me;
  if (onboardingState(next) === "choose") {
    setBusy(null);
    paint(next);
  } else {
    onDone(next);
  }
}

function showCardError(inviteId, message) {
  const card = [...els.list.querySelectorAll("[data-invite-id]")].find((el) => el.dataset.inviteId === inviteId);
  if (card) setError(card.querySelector('[data-role="card-error"]'), message);
  else setError(els.notice, message);
}

async function runAction(button, busyLabel, request, onError) {
  if (busy) return;
  clearErrors();
  setBusy(button, busyLabel);
  try {
    await settle(await request());
  } catch (err) {
    const { message, refresh } = actionErrorMessage(err);
    if (refresh) {
      try {
        const me = await loadMe();
        if (onboardingState(me) !== "choose") {
          onDone(me);
          return;
        }
        setBusy(null);
        paint(me);
      } catch {
        /* keep the current list; the message below still explains what happened */
      }
    }
    setBusy(null);
    onError(message);
  }
}

els.list.addEventListener("click", (e) => {
  const button = e.target.closest("[data-action]");
  if (!button || button.disabled) return;
  const id = button.closest("[data-invite-id]").dataset.inviteId;
  const path = `/onboarding/invites/${encodeURIComponent(id)}/${button.dataset.action}`;
  const busyLabel = t(button.dataset.action === "accept" ? "onboarding.joining" : "onboarding.declining");
  runAction(button, busyLabel, () => api.post(path, {}), (message) => showCardError(id, message));
});

els.skip.addEventListener("click", () => {
  runAction(els.skip, t("onboarding.starting"), () => api.post("/onboarding/skip", {}), (message) => setError(els.skipError, message));
});

els.signOut.addEventListener("click", async () => {
  if (busy) return;
  clearErrors();
  setBusy(els.signOut, t("onboarding.signingOut"));
  try {
    await signOut(auth);
    setBusy(null);
  } catch {
    setBusy(null);
    setError(els.signOutError, t("onboarding.signOutFailed"));
  }
});

window.addEventListener("transportos:locale-change", () => {
  if (!screen.hidden && shownMe && !busy) paint(shownMe);
});

export function renderOnboarding(me, options = {}) {
  onDone = options.onDone || (() => {});
  clearErrors();
  setBusy(null);
  paint(me);
}
